import React, { useEffect, useState } from "react";
import subscriptionService from "../../api/services/subscriptionService";
import toast from "react-hot-toast";
import Loading from "../../components/Loading";
import { FilterSection, ActiveFilterPills } from "../../components/UserProfiles";
import {
  useSubscriptionHistory,
  SubscriptionHistoryTable,
  SubscriptionHistoryPagination,
} from "../../components/SubscriptionHistory";
import { getStatusBadgeColor, getStatusLabel } from "../../components/SubscriptionHistory/SubscriptionHistoryTable";

const SUBSCRIPTION_PILL_CLASSES = {
  user_id: "bg-purple-100 text-purple-800",
  name: "bg-slate-100 text-slate-800",
  email: "bg-cyan-100 text-cyan-800",
  contactNo: "bg-orange-100 text-orange-800",
  date_from: "bg-blue-100 text-blue-800",
  date_to: "bg-green-100 text-green-800",
  subscription: "bg-indigo-100 text-indigo-800",
  source: "bg-amber-100 text-amber-800",
  currency: "bg-teal-100 text-teal-800",
};

function getSubscriptionPillClassName(key) {
  return SUBSCRIPTION_PILL_CLASSES[key] || "bg-gray-100 text-gray-800";
}

const Subscription = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const tabs = [
    { key: "All", label: "All" },
    { key: "Weekly", label: "Weekly" },
    { key: "Monthly", label: "Monthly" },
    { key: "NaseebiUsers", label: "Naseebi Users" },
    { key: "OtherUsers", label: "Other Users" },
    { key: "History", label: "History" },
  ];

  const {
    historyFilters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    subscriptionHistory,
    currentPage: historyPage,
    setCurrentPage: setHistoryPage,
    totalPages: historyTotalPages,
    totalItems: historyTotalItems,
    loadingHistory,
    historyError,
    limit: historyLimit,
    fetchSubscriptionHistory,
  } = useSubscriptionHistory({ enabled: activeTab === "History" });

  useEffect(() => {
    if (activeTab === "History") return;
    const getSubscriptions = async () => {
      try {
        setIsLoading(true);

        // Determine duration and emailType based on activeTab
        let duration = null;
        let emailType = null;

        if (activeTab === "Weekly") {
          duration = "Weekly";
        } else if (activeTab === "Monthly") {
          duration = "Monthly";
        } else if (activeTab === "NaseebiUsers") {
          emailType = "naseebimail";
        } else if (activeTab === "OtherUsers") {
          emailType = "other";
        }

        const response = await subscriptionService.getAllPurchasedSubscriptions(
          {
            page: currentPage,
            limit: itemsPerPage,
            duration: duration,
            emailType: emailType,
          }
        );
        if (response?.responseCode === 200) {
          setSubscriptionData(response?.subscriptions);
          if (response?.pagination) {
            setCurrentPage(response.pagination.currentPage);
            setTotalPages(response.pagination.totalPages);
            setTotalItems(response.pagination.totalItems);
            setItemsPerPage(response.pagination.itemsPerPage);
          }
          // toast.success(response?.message);
        } else {
          toast.error(response?.message);
        }
      } catch (error) {
        toast.error("Failed to fetch subscriptions");
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    getSubscriptions();
  }, [currentPage, itemsPerPage, activeTab]);

  const isHistoryTab = activeTab === "History";

  // 🔍 Filter by search (name or email) - duration and email filtering is now done by API
  const tableData = subscriptionData.filter((sub) => {
    const fullName = `${sub.firstName || ""} ${
      sub.lastName || ""
    }`.toLowerCase();
    const email = sub.email?.toLowerCase() || "";
    const term = searchTerm.toLowerCase();
    return fullName.includes(term) || email.includes(term);
  });

  return (
    <div className="p-4 sm:p-6 w-full min-w-0 overflow-x-hidden bg-gray-50/60 min-h-[calc(100vh-4rem)]">
      {/* Tab navigation - clear separation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 bg-white rounded-t-xl shadow-sm px-2 sm:px-4 pt-3 pb-0">
          <nav className="flex gap-1 flex-wrap" aria-label="Subscription tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveTab(tab.key);
                  if (tab.key !== "History") setCurrentPage(1);
                }}
                className={`px-4 py-2.5 rounded-t-lg font-medium text-sm transition-all duration-200 shrink-0 ${
                  activeTab === tab.key
                    ? "bg-red-500 text-white shadow-md -mb-px border border-b-0 border-red-500"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-transparent"
                }`}>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* History tab only: no subscription filters, just History content */}
      {isHistoryTab ? (
        <div className="bg-white rounded-b-xl rounded-t-xl sm:rounded-t-none shadow-md border border-t-0 border-gray-200 overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">
                  Subscription History
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  User-based history: each row is one purchase. Source: Manual (in-app) or RevenueCat (store).
                </p>
              </div>
              {getActiveFiltersCount() > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <ActiveFilterPills
              activeKeys={activeFilterEntries}
              filters={historyFilters}
              getLabel={getFilterLabel}
              onRemove={removeFilter}
              label="Active Filters:"
              getPillClassName={getSubscriptionPillClassName}
            />
            <FilterSection
              primaryFields={primaryFields}
              secondaryFields={[]}
              values={historyFilters}
              onChange={(e) => handleFilterChange(e.target.name, e.target.value)}
              onApply={() => setHistoryPage(1)}
              onClear={clearFilters}
              applyLabel="Apply"
              clearLabel="Clear All"
            />
            <SubscriptionHistoryTable
              items={subscriptionHistory}
              isLoading={loadingHistory}
              error={historyError}
              onRetry={() => fetchSubscriptionHistory(historyPage, historyFilters)}
            />
            <SubscriptionHistoryPagination
              currentPage={historyPage}
              totalPages={historyTotalPages}
              totalItems={historyTotalItems}
              limit={historyLimit}
              onPageChange={setHistoryPage}
            />
          </div>
        </div>
      ) : (
        /* Subscription tabs (All, Weekly, etc.): filters and table inside one card */
        <div className="bg-white rounded-b-xl sm:rounded-t-none rounded-xl shadow-md border border-t-0 border-gray-200 overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">
              Current Subscriptions
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              View and search active subscriptions by plan and user type.
            </p>
            {/* Search lives only inside subscription tab content */}
            <div className="mt-4">
              <label htmlFor="subscription-search" className="sr-only">Search by name or email</label>
              <input
                id="subscription-search"
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full max-w-md text-sm border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 shadow-sm"
                aria-label="Search by name or email"
              />
            </div>
          </div>
          <div className="p-0 sm:p-2">
            {isLoading ? (
              <div className="py-16">
                <Loading />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50/80 text-gray-700">
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">User Name</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Email</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Plan</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Duration</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Days</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Price</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Start</th>
                        <th className="px-4 py-3 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">End</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tableData.length > 0 ? (
                        tableData.map((sub) => (
                          <tr
                            key={sub.userSubscriptionId}
                            className="hover:bg-gray-50/80 transition-colors text-sm"
                          >
                            <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                              {sub.firstName} {sub.lastName}
                            </td>
                            <td className="px-4 py-3 text-gray-600 max-w-[140px] sm:max-w-[200px] truncate">
                              {sub.email}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-800">{sub.name}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                                  sub.status
                                )}`}>
                                {getStatusLabel(sub.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600">{sub.duration}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600">{sub.duration_days}</td>
                            <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                              {sub.currency} {sub.amount}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                              {sub.start_date ? new Date(sub.start_date).toLocaleDateString() : "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                              {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="px-4 py-12 text-center text-sm text-gray-500">
                            No subscriptions found for &quot;{activeTab}&quot;
                            {searchTerm ? ` (search: "${searchTerm}")` : ""}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 bg-gray-50/50 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700 order-2 sm:order-1">
                      Page <span className="font-bold text-gray-900">{currentPage}</span> of{" "}
                      <span className="font-bold text-gray-900">{totalPages}</span>
                      {" "}(<span className="font-bold text-red-600">{totalItems}</span> total)
                    </p>
                    <div className="flex gap-2 items-center order-1 sm:order-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600 min-w-[100px] text-center">
                        {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} – {Math.min(currentPage * itemsPerPage, totalItems)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
